import React, { useMemo, Suspense } from 'react'
import Svg from 'react-native-svg'

import { styled, getTokenValue, useTheme } from 'tamagui'

import { IconContext } from './context'

const getTokenPrimitiveValue = (
  token: string,
  group: 'size' | 'color'
): string | number | undefined => {
  const value: unknown = getTokenValue(
    token as Parameters<typeof getTokenValue>[0],
    group
  )

  return typeof value === 'string' || typeof value === 'number'
    ? value
    : undefined
}

const getThemeColor = (
  theme: ReturnType<typeof useTheme>,
  color: string
): string | undefined => {
  const themeKey = color.startsWith('$') ? color.slice(1) : undefined
  if (!themeKey) return undefined

  const themeRecord = theme as unknown as Record<string, { val?: unknown }>
  const themeValue = themeRecord[themeKey]?.val

  return typeof themeValue === 'string' ? themeValue : undefined
}

const StyledSVG = styled(Svg, {
  variants: {
    size: {
      '...size': (size) => {
        const iconSize =
          typeof size === 'string'
            ? getTokenPrimitiveValue(size, 'size')
            : undefined

        return {
          width: iconSize,
          height: iconSize,
          minWidth: iconSize,
          minHeight: iconSize,
        }
      },
    },
  },

  defaultVariants: {
    size: '$5',
  },
} as const)

export const IconWrapper = StyledSVG.styleable<{
  color?: string
}>(({ children, color, ...props }, ref) => {
  const theme = useTheme()
  const contextValue = useMemo(() => {
    if (!color) {
      return { color: undefined }
    }

    // Try theme tokens first (e.g. $color, $colorMuted) for dark mode support
    const themeColor = getThemeColor(theme, color)
    if (themeColor) {
      return { color: themeColor }
    }

    const tokenColor = getTokenPrimitiveValue(color, 'color')

    return {
      color: tokenColor ? String(tokenColor) : color,
    }
  }, [color, theme])

  return (
    <StyledSVG
      viewBox='0 0 24 24'
      //   xmlns='http://www.w3.org/2000/svg'
      {...props}
      ref={ref}
    >
      <IconContext.Provider value={contextValue}>
        <Suspense>{children}</Suspense>
      </IconContext.Provider>
    </StyledSVG>
  )
})
